import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useMsal } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"
import RequiredAuth from './SecureRoute'
import Home from './pages/Home'
import Reset from './pages/Reset'
import Login from './pages/Login'
import ModuleDetail from './pages/modules/ModuleDetail'
import ModuleForm from './pages/modules/ModuleForm'
import ModuleList from './pages/modules/ModuleList'
import StyleGuide from './pages/StyleGuide'
import PageNotAvailable from './pages/PageNotAvailable'
import Layout from './pages/Layout'
import Verification from "./pages/layouts/verifications/Verification"
import PaymentProcessorSplit from "./pages/layouts/splits/PaymentProcessorSplit"
import BankVerificationProviderSplit from "./pages/layouts/splits/BankVerificationProviderSplit"
import LeadBuyManagement from "./pages/layouts/splits/LeadBuyManagement"
import Report from "./pages/Report"
import PaymentSchedule from "./pages/layouts/payment_schedule/PaymentSchedule"
import UserNotifications from "./pages/layouts/UserNotifications"
import SendEmail from "./pages/SendEmail";
import WireApprovalQueue from "./pages/layouts/payment/WireApprovalQueue"
import WithdrawnLeadsWithAdverseAction from "./pages/layouts/lead/WithdrawnLeadsWithAdverseAction"
import CustomerMergeTool from "./pages/layouts/customer/CustomerMergeTool"
import TestSetup from './pages/TestSetup'
import TestFinance from './pages/TestFinance'
import PromotionCodeCustomer from "./pages/PromotionCodeCustomer";
import LoanRefinance from './pages/layouts/loan/LoanRefinance'
import BulkUncollectibleLoanRelease from './pages/layouts/loan/BulkUncollectibleLoanRelease'
import BulkCheckEntry from "./pages/layouts/bulk_check/BulkCheckEntry";
import DevToolbox from "./pages/DevToolbox";
import InquirySearch from "./pages/layouts/report/InquirySearch"
import LeadFunnelBlocks from "./pages/layouts/LeadFunnelBlocks"
import LeadFunnelBlockChangeRequests from "./pages/layouts/LeadFunnelBlockChangeRequests"
import LeadFunnelBlockChangeRequest from "./pages/layouts/LeadFunnelBlockChangeRequest"
import Campaign from './pages/layouts/campaign/Campaign'
import ModuleQueue from './pages/layouts/module_queue/ModuleQueue'
import DecisionLogicResults from './pages/layouts/DecisionLogicResults'
import ChirpResults from './pages/layouts/ChirpResults'
import DecisionLogicApprovalQueue from './pages/layouts/decision_logic/DecisionLogicApprovalQueue'

/**
 * The redirectUri is the app root, so Azure returns here with the auth `#code` hash.
 * MSAL's handleRedirectPromise (run by MsalProvider) needs to read that hash before
 * anything navigates away — a client-side <Navigate> strips the fragment and the login
 * never completes, which produces the redirect loop. Hold on the root until MSAL reports
 * no interaction in progress, then send the user to /home.
 */
function RootRedirect() {
  const { inProgress } = useMsal()

  if (inProgress !== InteractionStatus.None) {
    return null
  }

  // return <Navigate to="/home" replace />
  return <Login />
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/reset" element={<Reset />} />
      <Route element={<Layout />}>
        <Route path="/404" element={<PageNotAvailable type={404} />} />
        <Route path="/403" element={<PageNotAvailable type={403} />} />
        <Route path="/maintenance" element={<PageNotAvailable type={'Maintenance'} />} />

        <Route element={<RequiredAuth />}>
          <Route path="/home" element={<Home />} />
          <Route path="/style_guide" element={<StyleGuide />} />

          {/* Custom Views */}
          <Route path="/manual_verification/:id" element={<Verification />} />
          <Route path="/payment_processor_split" element={<PaymentProcessorSplit />} />
          <Route path="/bank_verification_provider_split" element={<BankVerificationProviderSplit />} />
          <Route path="/lead_buy_management" element={<LeadBuyManagement />} />
          <Route path="/test_setup" element={<TestSetup />} />
          <Route path="/bulk_check_entry" element={<BulkCheckEntry />} />
          <Route path="/bulk_check_entry/:id" element={<BulkCheckEntry />} />
          <Route path="/dev_toolbox" element={<DevToolbox />} />
          {/* @TODO: Future Update: Flush this out as a bank account dashboard? */}
          {/*<Route path="/test_finance" element={<TestFinance />} />*/}
          <Route path="/payment_schedule/:id" element={<PaymentSchedule />} />
          <Route path="/loan_refinance/:id" element={<LoanRefinance />} />
          <Route path="/bulk_uncollectible_loan_release" element={<BulkUncollectibleLoanRelease />} />
          <Route path="/user/notification" element={<UserNotifications />} />
          <Route path="/report" element={<Report />} />
          <Route path="/tribal_report" element={<Report type={'tribal'} />} />
          <Route path="/send_email" element={<SendEmail />} />
          <Route path="/wire_approval_queue" element={<WireApprovalQueue />} />
          <Route path="/withdrawn_leads" element={<WithdrawnLeadsWithAdverseAction />} />
          <Route path="/customer_merge_tool" element={<CustomerMergeTool />} />
          <Route path="/promotion_code_customer" element={<PromotionCodeCustomer />} />
          <Route path="/inquiry_search" element={<InquirySearch />} />
          <Route path="/lead_funnel_blocks" element={<LeadFunnelBlocks />} />
          <Route path="/lead_funnel_block_change_requests" element={<LeadFunnelBlockChangeRequests />} />
          <Route path="/lead_funnel_block_change_requests/:id" element={<LeadFunnelBlockChangeRequest />} />
          <Route path="/decision_logic_results/:id" element={<DecisionLogicResults />} />
          <Route path="/chirp_results/:id" element={<ChirpResults />} />
          <Route path="/decision_logic_approval_queue" element={<DecisionLogicApprovalQueue />} />

          {/* Module Queue*/}
          <Route path="/module_queue/:module/:action_type" element={<ModuleQueue />} />

          {/* Campaign module override with same route*/}
          <Route path="/campaign/form" element={<Campaign />} />
          <Route path="/campaign/:id" element={<Campaign />} />

          {/* Module Routes - Must be at the END */}
          <Route path="/:module_name" element={<ModuleList />} />
          <Route path="/:module_name/:id" element={<ModuleDetail />} />
          <Route path="/:module_name/form" element={<ModuleForm />} />
        </Route>
        <Route path="*" element={<PageNotAvailable type={404} />} />
      </Route>
    </Routes>
  )
}

export default AppRoutes
